import React, { useRef } from "react";
import { useHistory } from "react-router-dom";
import useLastVisitedRow from "@Util/hook/useLastVisitedRow";
import { PageLayout } from "@Local/Components/Layout";
import { OverviewListLayout, PageSortAndTotalBox, OverviewListToolbar } from "@Local/Components/OverviewList";
import { CyclePurchaseOverviewSearchBar } from "@Local/CyclePurchase/Overview";
import useCyclePurchaseOverviewApi from "@Apis/useCyclePurchaseOverviewApi";
import { getCyclePurchaseOverviewRowLabel } from "@Local/CyclePurchase/Overview/CyclePurchaseOverviewTemplate";
import { MemberOrderDetailsDialogBox } from "@Local/CyclePurchase/Overview/Dialog";
import { CYCLE_PURCHASE_DETAIL_MAIN_ROUTER_URL } from "@Router/routerPath";
import useOverview from "@Util/hook/useOverview";
import useOptions from "@Util/hook/useOptions";

const CyclePurchaseOverviewPage = () => {
	const { postCyclePurchaseOverviewApi, getCyclePurchaseOverviewOptionsApi } = useCyclePurchaseOverviewApi();
	const { options } = useOptions({
		getOptionsApi: getCyclePurchaseOverviewOptionsApi,
		isFetchOnMount: true,
	});
	const history = useHistory();
	const currentSearch = history.location?.search || ""; // 目前搜尋條件

	const { overviewData, getOverviewData, updateOverviewPage, updateOverviewSort, searchParams } = useOverview({
		getOverviewApi: postCyclePurchaseOverviewApi,
		defaultSearchParams: {
			clubID: 1,
			shippingDay: 99,
			cycleOrderType: 1,
			productName: null,
			memberSearch: null,
			sortBy: 1,
			pageIndex: 1,
			pageSize: 10,
		},
		isFetchOnMount: true,
		enableQueryString: true,
	});

	const { saveLastVisited, bodyRowClass } = useLastVisitedRow({
		list: overviewData?.list,
		rowKey: "cycleOrderID",
		storageKey: "lastVisitedCycleOrderId",
	});

	// 導向循環訂單明細頁，並保留當前搜尋條件
	const pushToCyclePurchaseDetail = cycleOrderID => {
		saveLastVisited(cycleOrderID);
		history.push({
			pathname: CYCLE_PURCHASE_DETAIL_MAIN_ROUTER_URL(cycleOrderID),
			search: currentSearch,
		});
	};

	const refreshRef = useRef(getOverviewData);
	const dialogRef = useRef(null);
	const headerRow = getCyclePurchaseOverviewRowLabel(pushToCyclePurchaseDetail, overviewData, dialogRef, getOverviewData);
	const showToolbar = overviewData?.list?.length; // 是否顯示工具列

	const handlePageChange = (e, page) => {
		updateOverviewPage(page);
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const handleSortChange = value => {
		updateOverviewSort(value);
	};

	const handleSearchBarParams = par => {
		const params = { ...par, sortBy: searchParams.sortBy, pageIndex: 1, pageSize: 10 };
		getOverviewData(params);
	};

	return (
		<PageLayout>
			<PageLayout.Header title="循環訂單總覽" />
			<OverviewListLayout
				contextValue={{
					refresh: refreshRef.current,
					setRefresh: fn => (refreshRef.current = fn),
				}}
			>
				<CyclePurchaseOverviewSearchBar getOverviewData={handleSearchBarParams} sourceData={searchParams} options={options} />
				{showToolbar ? (
					<OverviewListToolbar>
						<PageSortAndTotalBox
							pageIndex={overviewData?.pagination?.pageIndex}
							pageSize={overviewData?.pagination?.pageSize}
							totalCount={overviewData?.pagination?.totalCount}
							sortDefaultValue={searchParams?.sortBy || 1}
							sortOptions={options.cycleOrderOrderByOptions}
							sortOnChange={handleSortChange}
							isCurrentPageLastIndex
							isSort
						/>
					</OverviewListToolbar>
				) : null}
				<OverviewListLayout.Table
					headerRow={headerRow}
					list={overviewData?.list || []}
					tableRowBodyProps={{ dialogRef: null }}
					totalPage={overviewData?.pagination?.totalPage}
					pageIndex={overviewData?.pagination?.pageIndex}
					onChangePage={handlePageChange}
					bodyRowClass={bodyRowClass}
					isPagination
				/>
			</OverviewListLayout>
			<MemberOrderDetailsDialogBox ref={dialogRef} />
		</PageLayout>
	);
};

export default CyclePurchaseOverviewPage;
